// Confirm dialog for "Send override as a template change". Renders next to
// the existing Download / Remove actions when a template has been overridden
// for a submission. The override file becomes the `s3FileId` on a new
// EDIT_TEMPLATE template request so the support team can incorporate the
// override's changes back into the system template, closing the loop
// between submission-level overrides and template-level edits.
import {
  PriorityHighRounded as PriorityIcon,
  SendRounded as SubmitIcon,
} from "@mui/icons-material";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as logger from "common/logger";
import Button from "components/common/Button";
import { useEffect, useState } from "react";
import { useCreateTemplateRequestMutation } from "services/docGenService";

const DOCX_MIMETYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const SendOverrideToTemplateRequestsDialog = ({
  open,
  onClose,
  template,
  overrideFileId,
  submissionId,
  layerId,
  onSent,
}) => {
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createTemplateRequest] = useCreateTemplateRequestMutation();

  // Reset state every time the dialog opens so a previous error or comment
  // doesn't bleed into the next override the user looks at.
  useEffect(() => {
    if (open) {
      setDescription("");
      setPriority("MEDIUM");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!template?.id || !overrideFileId) return;
    setSubmitting(true);
    setError(null);

    // Pin the submission context into the description so the support team
    // can trace where the override came from. User-supplied notes go below
    // the auto-generated trail.
    const provenance = layerId
      ? `Submitted from submission ${submissionId} (layer ${layerId}) as a template override.`
      : `Submitted from submission ${submissionId} as a template override.`;
    const fullDescription = description.trim()
      ? `${provenance}\n\n${description.trim()}`
      : provenance;

    try {
      await createTemplateRequest({
        requestType: "EDIT_TEMPLATE",
        templateId: template.id,
        templateName: template.title ?? "",
        title: `Override from submission: ${template.title ?? template.id}`,
        description: fullDescription,
        priority,
        status: "SUBMITTED",
        s3FileId: overrideFileId,
        fileType: DOCX_MIMETYPE,
        placements: [],
        annotations: [],
        customFields: [],
      }).unwrap();
      onSent?.();
      onClose?.();
    } catch (e) {
      logger.exception(e, {
        breadcrumb: {
          action: "send-override-to-template-requests",
          templateId: template.id,
          submissionId,
        },
      });
      setError("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={"sm"}
      fullWidth
    >
      <DialogTitle>{"Send override to template requests"}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <Typography variant={"body2"} color={"text.secondary"}>
            {`Creates a new template request to update the system template "${
              template?.title ?? template?.id ?? ""
            }" using this override as the edited version. The support team will see this in their queue alongside other edit requests.`}
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={"Notes for the support team (optional)"}
            placeholder={
              "What changed in this override? Anything specific to call out..."
            }
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <TextField
            fullWidth
            select
            label={"Priority"}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            SelectProps={{ native: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position={"start"}>
                  <PriorityIcon
                    sx={{
                      color:
                        priority === "HIGH"
                          ? "error.main"
                          : priority === "MEDIUM"
                          ? "warning.main"
                          : "success.main",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          >
            <option value={"LOW"}>
              {"Low - No rush, whenever convenient"}
            </option>
            <option value={"MEDIUM"}>{"Medium - Standard turnaround"}</option>
            <option value={"HIGH"}>{"High - Urgent, needed soon"}</option>
          </TextField>

          {error && <Alert severity={"error"}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant={"text"} onClick={onClose} disabled={submitting}>
          {"Cancel"}
        </Button>
        <Button
          startIcon={<SubmitIcon />}
          onClick={handleSubmit}
          isLoading={submitting}
          disabled={!overrideFileId}
        >
          {"Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendOverrideToTemplateRequestsDialog;
